// src/components/booking/CancelConfirmation.js
// Cancel confirmation flow — shows refund preview, optional reason, and confirm/keep buttons.
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const REGION = 'us-west1';

const REASON_OPTIONS = [
  { value: '', label: 'Why are you cancelling? (optional)' },
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'personal', label: 'Personal reasons' },
  { value: 'no_longer_needed', label: 'No longer need this session' },
  { value: 'financial', label: 'Financial reasons' },
  { value: 'found_another', label: 'Found another practitioner' },
  { value: 'other', label: 'Other' },
];

function calculateRefundPreview(booking) {
  const price = booking.service?.price || 0;
  const priceCents = Math.round(price * 100);
  const policy = booking.cancellationPolicy;

  if (!policy || priceCents === 0) {
    return { percentage: 100, amount: priceCents, isFullRefund: true, isInsideWindow: false, policyWindowHours: 0 };
  }

  const sessionTime = booking.dateTime?._seconds
    ? new Date(booking.dateTime._seconds * 1000)
    : new Date(booking.dateTime);
  const now = new Date();
  const hoursUntil = (sessionTime - now) / (1000 * 60 * 60);

  let policyWindowHours = 24;
  if (policy.timeframe === '48h') policyWindowHours = 48;
  else if (policy.timeframe === '72h') policyWindowHours = 72;
  else if (policy.timeframe === '1week') policyWindowHours = 168;
  else if (policy.timeframe === 'custom' && policy.customTimeframe) {
    policyWindowHours = parseFloat(policy.customTimeframe) || 24;
  }

  const isInsideWindow = hoursUntil <= policyWindowHours;

  let percentage = 100;
  if (isInsideWindow) {
    if (policy.refundPercentage === 'full') percentage = 100;
    else if (policy.refundPercentage === '50') percentage = 50;
    else if (policy.refundPercentage === 'none') percentage = 0;
    else if (policy.refundPercentage === 'custom' && policy.customRefund) {
      percentage = parseFloat(policy.customRefund) || 0;
    } else {
      percentage = 0;
    }
  }

  return {
    percentage,
    amount: Math.round((priceCents * percentage) / 100),
    isFullRefund: percentage === 100,
    isInsideWindow,
    policyWindowHours,
  };
}

function formatWindowLabel(hours) {
  if (hours >= 168) return '1 week';
  return `${hours} hours`;
}

const CancelConfirmation = ({ booking, bookingId, token, onCancelled, onBack }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);

  const refund = calculateRefundPreview(booking);
  const refundDollars = (refund.amount / 100).toFixed(2);
  const hasPaid = (booking.service?.price || 0) > 0 && booking.stripePaymentIntentId;

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      const functions = getFunctions(undefined, REGION);
      const cancelBookingFn = httpsCallable(functions, 'cancelBooking');
      const result = await cancelBookingFn({
        bookingId,
        token,
        cancelledBy: 'client',
        reason: reason ? {
          category: reason,
          details: details.trim() || null,
        } : null,
      });
      onCancelled(result.data);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setCancelling(false);
    }
  };

  return (
    <div style={{ animation: 'bmFadeIn 0.3s ease' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
        Cancel this session?
      </h2>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
        This action cannot be undone.
      </p>

      {/* Refund info */}
      {hasPaid && (
        <div style={{
          padding: '16px 20px',
          borderRadius: '8px',
          marginBottom: '24px',
          background: refund.isFullRefund ? '#ECFDF5' : '#FFFBEB',
          border: refund.isFullRefund ? '1px solid #A7F3D0' : '1px solid #FDE68A',
        }}>
          {refund.isFullRefund ? (
            <p style={{ fontSize: '14px', color: '#065F46', margin: 0 }}>
              ✓ You'll receive a full refund of <strong>${refundDollars}</strong>
            </p>
          ) : refund.percentage > 0 ? (
            <>
              <p style={{ fontSize: '14px', color: '#92400E', margin: '0 0 4px' }}>
                ⚠ Per {booking.practitionerName}'s cancellation policy, cancellations within {formatWindowLabel(refund.policyWindowHours)} of the session receive a {refund.percentage}% refund.
              </p>
              <p style={{ fontSize: '14px', color: '#92400E', margin: 0, fontWeight: 600 }}>
                Your refund: ${refundDollars}
              </p>
            </>
          ) : (
            <p style={{ fontSize: '14px', color: '#92400E', margin: 0 }}>
              ⚠ Per {booking.practitionerName}'s cancellation policy, cancellations within {formatWindowLabel(refund.policyWindowHours)} of the session are non-refundable.
            </p>
          )}
        </div>
      )}

      {/* Reason (optional) */}
      <div style={{ marginBottom: '16px' }}>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={cancelling}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '14px',
            color: reason ? '#111827' : '#9CA3AF',
            background: '#fff',
            fontFamily: "'Montserrat', sans-serif",
            cursor: cancelling ? 'not-allowed' : 'pointer',
            outline: 'none',
          }}
        >
          {REASON_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {reason && (
        <div style={{ marginBottom: '24px', position: 'relative' }}>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 300))}
            placeholder="Anything else you'd like to share? (optional)"
            disabled={cancelling}
            rows={3}
            style={{
              width: '100%',
              padding: '12px 14px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#111827',
              fontFamily: "'Montserrat', sans-serif",
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <span style={{
            position: 'absolute',
            bottom: '8px',
            right: '12px',
            fontSize: '11px',
            color: details.length >= 280 ? '#DC2626' : '#9CA3AF',
          }}>
            {details.length}/300
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          <p style={{ fontSize: '14px', color: '#991B1B', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={handleCancel}
          disabled={cancelling}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: '#DC2626',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: cancelling ? 'not-allowed' : 'pointer',
            fontFamily: "'Montserrat', sans-serif",
            opacity: cancelling ? 0.7 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {cancelling ? 'Cancelling...' : 'Cancel this session'}
        </button>
        <button
          onClick={onBack}
          disabled={cancelling}
          style={{
            width: '100%',
            padding: '14px 24px',
            background: 'transparent',
            color: '#374151',
            border: '1px solid #D1D5DB',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: 500,
            cursor: cancelling ? 'not-allowed' : 'pointer',
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          Keep my session
        </button>
      </div>
    </div>
  );
};

export default CancelConfirmation;
